import { v5 as uuidv5 } from 'uuid';

/**
 * Opaque contact references for URLs.
 *
 * DataCentral contact ids are sequential integers. Exposing them in a URL
 * (`members/9159`) invites enumeration/brute-forcing of other contacts. Mirroring
 * how @services turns raw OpenNebula ids into stable UUIDs (`Digest::UUID.uuid_v5`),
 * we derive a deterministic UUIDv5 from the org-scoped contact id. It is:
 *   - deterministic  — same (customer, contact) → same ref, so links are stable;
 *   - one-way        — a SHA-1 based UUIDv5 can't be decoded back to the int;
 *   - non-sequential — adjacent ids hash to unrelated UUIDs, so you can't guess
 *                      a neighbour's URL.
 *
 * Resolution is intentionally list-scoped: {@link findContactByRef} matches a ref
 * only against contacts in the caller's OWN org list, so a ref for another org's
 * contact never resolves — defense-in-depth on top of the API's own authz.
 */

/** Fixed application namespace for contact refs (any stable UUID works). */
const CONTACT_NAMESPACE = 'a3f1c2e4-7b8d-5c6a-9e0f-1d2c3b4a5e6f';

/** Deterministic opaque URL ref for a contact, scoped to its organization. */
export function contactRef(customerId: number, contactId: number): string {
  return uuidv5(`${customerId}:${contactId}`, CONTACT_NAMESPACE);
}

/**
 * Resolve a URL ref back to a contact by recomputing the ref for each contact in
 * the caller's org list and matching — the same recompute-and-match @services
 * uses to map a UUID back to a raw OpenNebula id. Returns undefined if no contact
 * in the list produces this ref.
 */
export function findContactByRef<T extends { contact_id: number }>(
  list: T[],
  customerId: number,
  ref: string,
): T | undefined {
  return list.find((c) => contactRef(customerId, c.contact_id) === ref);
}
